import { Navigate, useLocation } from "react-router-dom";
import AppLoadingScreen from "@/components/AppLoadingScreen";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditContext";
import { hasVpcApiKey, isVpcMode } from "@/utils/vpcMode";

/**
 * Route guard that blocks non-admin users before the page mounts.
 * - Not authenticated → /login, carrying the requested path as ?returnTo so
 *   login lands them where they were headed rather than on the home page
 * - Authenticated but not admin → /
 * - Auth/profile still loading → spinner
 *
 * `firebaseOnly` marks a page whose data lives behind /api/admin/*. Those
 * routes depend on validate_firebase_user (admin_routes.py), which requires an
 * Authorization header, so a VPC session -- which authenticates with X-API-Key
 * -- gets 422 on every request and the page renders an empty shell. Opt in per
 * route rather than blanket-blocking: most AdminRoute pages (the run view, the
 * query dashboard, data curation) read ordinary endpoints that go through
 * validate_auth and work fine with an API key.
 */
export default function AdminRoute({
  children,
  firebaseOnly = false,
}: {
  children: React.ReactNode;
  firebaseOnly?: boolean;
}) {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: creditsLoading } = useCredits();
  const location = useLocation();

  if (authLoading || creditsLoading) {
    return <AppLoadingScreen />;
  }

  // A Firebase-only page cannot load anything in VPC (see the note above), so
  // send the user home instead of showing them a shell that 422s.
  if (firebaseOnly && isVpcMode()) {
    return <Navigate to="/" replace />;
  }

  // VPC mode: if an api_key is present, trust it and render the protected
  // content. The backend still enforces authz — this only prevents the
  // Firebase-based frontend guards from redirecting to /login.
  if (hasVpcApiKey()) {
    return <>{children}</>;
  }

  if (!user) {
    // URLSearchParams encodes the value: an unescaped "#" would truncate the
    // query string and cost the caller their anchor.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?${new URLSearchParams({ returnTo })}`} replace />;
  }

  if (!profile || !profile.admin_mode) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
